/**
 * EditGroupModal Component
 *
 * Modal for editing workspace group name and description
 */

import React, { useState } from 'react';
import { Workspace, WorkspaceGroup } from '../../../types/workspace';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { X } from 'lucide-react';

interface EditGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspace: Workspace;
  group: WorkspaceGroup;
  onSuccess: () => void;
}

export const EditGroupModal: React.FC<EditGroupModalProps> = ({
  isOpen,
  onClose,
  workspace,
  group,
  onSuccess,
}) => {
  const { updateWorkspaceGroup } = useWorkspace();
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Nome do grupo é obrigatório');
      return;
    }

    setIsSubmitting(true);

    try {
      await updateWorkspaceGroup(workspace.id, group.id, {
        name: name.trim(),
        description: description.trim() || undefined,
      });

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao atualizar grupo');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setName(group.name);
      setDescription(group.description || '');
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 w-screen h-screen bg-black/60 z-40"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-[#2a2a2a] rounded-2xl shadow-2xl p-6 z-50">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-white">Editar Grupo</h2>
            <p className="text-sm text-[#888888] mt-1">Atualizar informações do grupo</p>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Group Name */}
          <div>
            <label htmlFor="edit-group-name" className="block text-sm font-medium text-[#cccccc] mb-2">
              Nome do Grupo *
            </label>
            <input
              id="edit-group-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Equipe de Marketing"
              disabled={isSubmitting}
              className="
                w-full px-4 py-3
                bg-[#1a1a1a] border border-[#333333]
                text-white placeholder-[#888888]
                rounded-lg
                focus:outline-none focus:border-[#F4CC04]
                disabled:opacity-50 disabled:cursor-not-allowed
              "
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="edit-description" className="block text-sm font-medium text-[#cccccc] mb-2">
              Descrição (Opcional)
            </label>
            <textarea
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descreva o propósito deste grupo..."
              rows={3}
              disabled={isSubmitting}
              className="
                w-full px-4 py-3
                bg-[#1a1a1a] border border-[#333333]
                text-white placeholder-[#888888]
                rounded-lg
                focus:outline-none focus:border-[#F4CC04]
                disabled:opacity-50 disabled:cursor-not-allowed
                resize-none
              "
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="
                px-4 py-2
                text-[#888888] hover:text-white
                hover:bg-[#333333]
                rounded-lg transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              className="
                px-4 py-2
                bg-[#F4CC04] hover:bg-[#D4B104]
                text-black font-medium
                rounded-lg transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center gap-2
              "
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                  <span>Salvando...</span>
                </>
              ) : (
                <span>Salvar Alterações</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default EditGroupModal;
